import React, { useCallback, useMemo, useState } from 'react';

import { useRouter } from 'expo-router';
import { useAddMappedEntryToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import { useMinimalRecentEntryAvailability } from 'features/menu/pages/recent-items-page/minimal-recent-items/use-minimal-recent-entry-availability';
import { logAddRecentItemToCartEvent } from 'features/order/utils/analytics';
import { useStoreContext } from 'features/store';
import { Pressable, StyleSheet } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { toaster } from 'components/toaster/toaster';
import { Box, Typography } from 'components/ui';
import { ItemAvailabilityStatus } from 'enums/menu';
import { IErrorModal } from 'hooks/use-error-modal';
import useErrorModal from 'hooks/use-error-modal';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { getDesignToken, shadows, spacing } from 'styles/constants';
import { routes } from 'utils/routing';

import { MinimalOrderAction } from './minimal-order-action';
import { OrderPointsEarned } from './order-points-earned';
import { RewardText } from './reward-text';
import { MinimalAccountOrder } from './types';

type MinimalOrderErrorModal = Pick<IErrorModal, 'message' | 'modalAppearanceEventMessage'>;

const MinimalOrder = ({ order }: { order: MinimalAccountOrder }) => {
  const isDesktop = useIsDesktopBreakpoint();
  const { store, fetchStore, isStoreOpenAndAvailable, noStoreSelected } = useStoreContext();
  const addMappedEntryToCart = useAddMappedEntryToCart();
  const { availability, hasSomeAvailableEntry } = useMinimalRecentEntryAvailability(
    order.minimalEntries
  );
  const router = useRouter();
  const [isReordering, setIsReordering] = useState(false);

  const [ErrorDialog, openErrorDialog] = useErrorModal<MinimalOrderErrorModal>({
    modalAppearanceEventMessage: 'Error: Fetch Store Failure',
  });

  const formattedDate = useMemo(
    () =>
      new Date(order.createdAt).toLocaleString(t('localeDateTime'), {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }),
    [order.createdAt]
  );

  const { itemCSV, numItems } = useMemo(() => {
    const csvForItem = order.minimalEntries.map(entry => entry.name).join(', ');
    const totalItemsOrdered = order.minimalEntries.reduce((acc, item) => acc + item.quantity, 0);

    const itemsCount = `${totalItemsOrdered} ${(totalItemsOrdered === 1
      ? t('item')
      : t('items')
    ).toLowerCase()}`;

    return { itemCSV: csvForItem, numItems: itemsCount };
  }, [order.minimalEntries]);

  const showOrderModal = useCallback(() => {
    router.navigate(routes.orderDetails(order.rbiOrderId));
  }, [order.rbiOrderId, router]);

  const handleFinishOrder = async () => {
    const orderStoreId = order.cart?.storeId ? order.cart.storeId.toString() : '';

    if (store.number && store.number !== orderStoreId) {
      try {
        await fetchStore(orderStoreId);
      } catch {
        return openErrorDialog({ message: t('couldNotLocateStore') });
      }
    }

    router.replace(`${routes.orderConfirmation}/${order.rbiOrderId}`);
  };

  const handleReorder = useCallback(() => {
    if (!isStoreOpenAndAvailable || noStoreSelected) {
      return;
    }

    const availableEntries = order.minimalEntries.filter(
      (_, index) => availability[index]?.itemAvailabilityStatus === ItemAvailabilityStatus.AVAILABLE
    );

    setIsReordering(true);

    const addedEntries = availableEntries.filter(entry => {
      const added = addMappedEntryToCart(entry);
      if (added) {
        logAddRecentItemToCartEvent(entry?.name ?? '', store);
      }
      return added;
    });

    const [firstEntry] = addedEntries;
    if (firstEntry) {
      const message =
        addedEntries.length === 1
          ? firstEntry.quantity > 1
            ? t('addToCartMultipleSuccess', {
                itemName: firstEntry.name,
                quantity: firstEntry.quantity - 1,
              })
            : t('addToCartSuccess', { itemName: firstEntry.name })
          : t('cartUpdatedRecentReorder', 'Your cart was updated');

      toaster.addToCartToast(message, firstEntry.image);
    }

    if (!addedEntries.length) {
      setIsReordering(false);
      return;
    }

    router.replace(routes.cart);
  }, [
    addMappedEntryToCart,
    availability,
    isStoreOpenAndAvailable,
    noStoreSelected,
    order.minimalEntries,
    router,
    store,
  ]);

  const itemsArr = useMemo(() => (itemCSV ? itemCSV.split(',') : []), [itemCSV]);
  const remainingItems = Math.max(itemsArr.length - 1, 0);
  const renderMoreItems = useCallback(
    () => (
      <Pressable onPress={() => showOrderModal()} style={styles.moreItems}>
        <Typography fontSize="sm" style={styles.moreItemsText}>
          {t('moreItems', { remainingItems })}
        </Typography>
      </Pressable>
    ),
    [remainingItems, showOrderModal]
  );

  const disabled = !hasSomeAvailableEntry || !isStoreOpenAndAvailable || noStoreSelected;
  const showTrackOrderModalPage = () =>
    router.navigate(`${routes.orderConfirmation}/${order.rbiOrderId}?dismissOnly=true`);

  return (
    <>
      <Box
        testID={`recent-order-${order.rbiOrderId}`}
        style={[styles.tile, isDesktop && styles.tileDesktop]}
      >
        <Box style={[styles.col, isDesktop && styles.colDesktop]} focusable>
          <Box style={styles.orderNumberWrapper}>
            {order.orderNumberFormatted && (
              <>
                <Typography variant="headerTwo" style={styles.orderNumberText}>
                  {`${t('orderNumber')}:`}
                </Typography>
                <Typography variant="headerTwo" style={styles.orderNumber}>
                  {order.orderNumberFormatted}
                </Typography>
              </>
            )}
          </Box>
          <Typography variant="headerTwo" style={styles.formattedDate}>
            {formattedDate}
          </Typography>
          <Box>
            <Box style={styles.subtitleRow}>
              <Typography variant="headerThree" style={styles.numItems}>
                {numItems}
              </Typography>
              {order.loyaltyTransaction && (
                <OrderPointsEarned
                  loyaltyTransaction={{
                    id: order.loyaltyTransaction.id,
                    pointsEarned: order.loyaltyTransaction.pointsEarned,
                    bonusPointsEarned: order.loyaltyTransaction.bonusPointsEarned,
                    pointsUsed: 0,
                    pointsBalance: 0,
                    rewardsUsed: 0,
                  }}
                />
              )}
            </Box>
          </Box>
          <Box style={styles.itemNameContainer}>
            {order.hasLoyaltySelection && <RewardText text={t('rewards')} />}
            <Box style={styles.items}>
              {itemsArr[0] ? <Typography>{itemsArr[0]}</Typography> : null}
              {remainingItems > 0 && renderMoreItems()}
            </Box>
          </Box>
        </Box>
        <Box style={[styles.col, isDesktop && styles.colDesktop]}>
          <Box style={styles.fullWidth}>
            <ActionButton
              testID="recent-order-view-details"
              variant={ActionButtonVariants.OUTLINE}
              fullWidth
              onPress={showOrderModal}
              aria-label={
                order.orderNumberFormatted
                  ? t('viewDetailsWithOrderNumber', { ticketNumber: order.orderNumberFormatted })
                  : t('viewDetails')
              }
            >
              {t('viewDetails')}
            </ActionButton>
            <Box style={styles.orderBox}>
              <MinimalOrderAction
                order={order}
                loading={isReordering}
                disabled={disabled}
                onTrack={showTrackOrderModalPage}
                onReorder={handleReorder}
                onFinish={handleFinishOrder}
              />
            </Box>
          </Box>
        </Box>

        <ErrorDialog />
      </Box>
    </>
  );
};

export default MinimalOrder;

const styles = StyleSheet.create({
  col: {
    width: '100%',
  },
  colDesktop: {
    width: '48%',
  },
  formattedDate: {
    marginBottom: spacing.$6,
    textTransform: getDesignToken('text-transform-headlines'),
  },
  fullWidth: {
    width: '100%',
  },
  itemNameContainer: {
    alignItems: 'flex-start',
    marginBottom: spacing.$6,
  },
  items: {
    paddingRight: spacing.$4,
  },
  moreItems: {
    paddingTop: spacing.$0_5,
  },
  moreItemsText: {
    color: getDesignToken('text-accent-order-number'),
  },
  numItems: {
    color: getDesignToken('text-default'),
    marginLeft: spacing.$1,
  },
  orderBox: {
    marginTop: spacing.$4,
    width: '100%',
  },
  orderNumber: {
    color: getDesignToken('text-accent-order-number'),
    fontSize: 16,
    margin: 0,
    paddingLeft: spacing.$1,
  },
  orderNumberText: {
    fontSize: 14,
    fontWeight: '400',
  },
  orderNumberWrapper: {
    flexDirection: 'row',
    marginHorizontal: 0,
    marginTop: spacing.$4,
  },
  subtitleRow: {
    flexDirection: 'row',
  },
  tile: {
    alignItems: 'center',
    backgroundColor: getDesignToken('background-pattern'),
    borderRadius: 16,
    justifyContent: 'center',
    marginVertical: spacing.$4,
    minHeight: spacing.$48,
    paddingBottom: spacing.$4,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$1,
    ...shadows.$1,
  },
  tileDesktop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});
